import { apiRequestNew } from '@/helpers/request'
import type { T_CertificatesListRes } from '@/types/responses'

const PENDING_RESTORE_LS_PREFIX = 'ERA_PENDING_RESTORE_CERTIFICATES'
export const PENDING_RESTORE_UPDATED_EVENT = 'era:pending-restore-updated'
const MAX_PENDING_RESTORE_AGE_MS = 72 * 60 * 60 * 1000

export type T_PendingRestoreItem = {
  cert: T_CertificatesListRes[number]
  movedAt: number
}

export const getPendingRestoreQueueKey = (userId?: number | null) => (
  `${PENDING_RESTORE_LS_PREFIX}_${userId || 0}`
)

const normalizePendingRestoreQueue = (raw: unknown): T_PendingRestoreItem[] => {
  if (!Array.isArray(raw)) return []
  const now = Date.now()

  const normalized = raw
    .map((item: any) => {
      const cert = item?.cert
      const movedAt = Number(item?.movedAt)
      if (!cert || typeof cert !== 'object') return null
      const certId = Number(cert.id)
      if (!(certId > 0)) return null
      const normalizedMovedAt = Number.isFinite(movedAt) && movedAt > 0 ? movedAt : Date.now()
      if ((now - normalizedMovedAt) > MAX_PENDING_RESTORE_AGE_MS) return null
      return {
        cert: { ...cert, id: certId },
        movedAt: normalizedMovedAt,
      } as T_PendingRestoreItem
    })
    .filter(Boolean) as T_PendingRestoreItem[]

  const dedupById = new Map<number, T_PendingRestoreItem>()
  for (const item of normalized.sort((a, b) => b.movedAt - a.movedAt)) {
    if (!dedupById.has(item.cert.id)) dedupById.set(item.cert.id, item)
  }
  return [ ...dedupById.values() ]
}

export const readPendingRestoreQueue = (userId?: number | null): T_PendingRestoreItem[] => {
  try {
    const raw = localStorage.getItem(getPendingRestoreQueueKey(userId))
    if (!raw) return []
    return normalizePendingRestoreQueue(JSON.parse(raw))
  } catch {
    return []
  }
}

export const writePendingRestoreQueue = (
  userId: number | null | undefined,
  items: T_PendingRestoreItem[],
) => {
  try {
    const key = getPendingRestoreQueueKey(userId)
    const normalized = normalizePendingRestoreQueue(items)
    const nextRaw = JSON.stringify(normalized)
    const prevRaw = localStorage.getItem(key)
    if (prevRaw === nextRaw) return

    localStorage.setItem(key, nextRaw)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(PENDING_RESTORE_UPDATED_EVENT))
    }
  } catch {
    // ignore localStorage failures
  }
}

export const upsertPendingRestoreQueueItem = (
  userId: number | null | undefined,
  nextItem: T_PendingRestoreItem,
) => {
  const prev = readPendingRestoreQueue(userId)
  const next = [
    nextItem,
    ...prev.filter((p) => p.cert.id !== nextItem.cert.id),
  ]
  writePendingRestoreQueue(userId, next)
}

export const removePendingRestoreQueueItem = (
  userId: number | null | undefined,
  certificateId: number,
) => {
  const prev = readPendingRestoreQueue(userId)
  const next = prev.filter((p) => p.cert.id !== certificateId)
  writePendingRestoreQueue(userId, next)
}

export const flushPendingRestoreQueueToServer = async (
  userId: number | null | undefined,
) => {
  const pending = readPendingRestoreQueue(userId)
    .sort((a, b) => a.movedAt - b.movedAt)

  let atLeastOneSucceeded = false
  let archivedIds: Set<number> | null = null
  let isArchivedPrecheckAvailable = false

  for (const item of pending) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) break
    if (!isArchivedPrecheckAvailable) {
      try {
        const archived = await apiRequestNew<T_CertificatesListRes>('GET', '/era/gift/certificate?archive=y&offset=0&limit=9999')
        archivedIds = new Set((archived || []).map((c) => Number(c.id)))
        isArchivedPrecheckAvailable = true
      } catch {
        // If precheck fails, fallback to direct restore attempt.
        archivedIds = null
        isArchivedPrecheckAvailable = true
      }
    }
    if (archivedIds && !archivedIds.has(Number(item.cert.id))) {
      // Already absent in archived slice: do not spam restore endpoint.
      continue
    }
    try {
      await apiRequestNew('GET', `/era/gift/certificate/${item.cert.id}/restore`)
      if (archivedIds) {
        archivedIds.delete(Number(item.cert.id))
      }
      atLeastOneSucceeded = true
    } catch (err: any) {
      // Idempotent behavior: treat "not found" as already restored/handled.
      if (Number(err?.response?.status) === 404) {
        atLeastOneSucceeded = true
        continue
      }
      // keep item in queue; next online cycle will retry
    }
  }

  return atLeastOneSucceeded
}
